import { useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import Input from "../../Components/Input";
import Radio from "../../Components/Radio";
import AdminContext from "../../Services/Admin";
import { canManageClosedProject } from "../../utils/auth";

const idr = (n) =>
	new Intl.NumberFormat("id-ID", {
		style: "currency",
		currency: "IDR",
		minimumFractionDigits: 0,
	}).format(Number(n) || 0);

const DetailProjectCloseModal = ({ data, handleClose, refresh }) => {
	const canManage = canManageClosedProject();
	const { getAllMaster, addMaster, loading } = useContext(AdminContext);

	const [isGoal, setIsGoal] = useState(false);
	const [incentiveId, setIncentiveId] = useState("");
	const [incentives, setIncentives] = useState([]);
	const [rows, setRows] = useState([]);

	useEffect(() => {
		setIsGoal(!!data?.is_goal);
		setIncentiveId(data?.m_project_incentive_type_id || "");
		setRows(
			(data?.project_points || []).map((p) => ({
				user_id: p?.user?.id ?? p?.m_user_id,
				name: p?.user?.name,
				points_earned: Number(p?.points_earned) || 0,
			})),
		);
	}, [data?.id]);

	useEffect(() => {
		if (!canManage) return;
		getAllMaster("master/incentive").then((res) => setIncentives(res?.data || []));
	}, [canManage]);

	const selectedIncentive = incentives.find((i) => String(i.id) === String(incentiveId));
	const baseBonus = selectedIncentive?.bonus ?? data?.incentive_type?.bonus ?? 0;
	const bonus = isGoal ? Number(baseBonus) || 0 : 0;

	const totalPoints = rows.reduce((sum, r) => sum + (Number(r.points_earned) || 0), 0);

	const setPoints = (userId, val) => {
		const value = Number(val) || 0;
		const capped = value > 10 ? 10 : value < 0 ? 0 : value;
		setRows((prev) => prev.map((r) => (r.user_id === userId ? { ...r, points_earned: capped } : r)));
	};

	const handleSave = async () => {
		const body = {
			is_goal: isGoal ? "true" : "false",
			m_project_incentive_type_id: incentiveId,
			contributors: rows.map((r) => ({ user_id: r.user_id, points: r.points_earned })),
		};
		const res = await addMaster(`project/close/adjust/${data?.id}`, body);
		if (res?.success) {
			toast.success("Closed project adjusted");
			refresh?.();
			handleClose?.();
		}
	};

	const handleReopen = async () => {
		if (!window.confirm("Reopen this project? It will return to the active project list.")) return;
		const res = await addMaster(`project/close/reopen/${data?.id}`, {});
		if (res?.success) {
			toast.success("Project reopened");
			refresh?.();
			handleClose?.();
		}
	};

	return (
		<>
			{canManage && (
				<div className="mb-5 rounded-lg border border-gray-200 bg-gray-50 p-4">
					<div className="mb-4">
						<label className="block mb-2 font-medium">Is project Goal?</label>
						<div className="flex">
							<Radio
								id="adjust-goal-yes"
								label="Yes"
								name="adjust-goal"
								value="true"
								checked={isGoal}
								onChange={() => setIsGoal(true)}
							/>
							<Radio
								id="adjust-goal-no"
								label="No"
								name="adjust-goal"
								value="false"
								checked={!isGoal}
								onChange={() => setIsGoal(false)}
							/>
						</div>
					</div>
					<div>
						<label htmlFor="adjust-incentive" className="block mb-2 font-medium">
							Incentive Type
						</label>
						<select
							id="adjust-incentive"
							className="border border-black text-gray-900 rounded-xl focus:ring-black focus:border-black block w-full p-3"
							value={incentiveId}
							onChange={(e) => setIncentiveId(e.target.value)}
						>
							<option value="">- Select incentive -</option>
							{incentives.map((i) => (
								<option key={i.id} value={i.id}>
									{i.name} ({idr(i.bonus)})
								</option>
							))}
						</select>
					</div>
				</div>
			)}

			<table className="w-full text-left">
				<thead className="border-t border-b border-black">
					<tr>
						<th scope="col" className="px-6 py-1">
							<div className="border-e border-black py-1">No</div>
						</th>
						<th scope="col" className="px-6 py-1">
							<div className="border-e border-black py-1">Name</div>
						</th>
						<th scope="col" className="px-6 py-1">
							<div className="border-e border-black py-1">Points</div>
						</th>
						<th scope="col" className="px-6 py-1">
							<div className="border-e border-black py-1">%</div>
						</th>
						{canManage && (
							<th scope="col" className="px-6 py-1">
								<div className="border-e border-black py-1">bonus</div>
							</th>
						)}
					</tr>
				</thead>
				<tbody className="font-light">
					{rows.map((item, index) => {
						const percentage = totalPoints > 0 ? (item.points_earned / totalPoints) * 100 : 0;
						const incentive = (percentage / 100) * bonus;
						return (
							<tr key={item.user_id ?? index}>
								<td className="px-6 py-4 w-[100px]">{index + 1}</td>
								<td className="px-6 py-4">
									<div className="font-semibold">{item?.name}</div>
								</td>
								<td className="px-6 py-4">
									{canManage ? (
										<div className="w-[110px]">
											<Input
												id={`points-${item.user_id}`}
												type="number"
												value={item.points_earned}
												onChange={(e) => setPoints(item.user_id, e.target.value)}
											/>
										</div>
									) : (
										<div className="font-semibold">{item.points_earned}</div>
									)}
								</td>
								<td className="px-6 py-4">
									<div className="font-semibold">{percentage.toFixed(2)}%</div>
								</td>
								{canManage && (
									<td className="px-6 py-4">
										<div className="font-semibold">{idr(incentive)}</div>
									</td>
								)}
							</tr>
						);
					})}
				</tbody>
			</table>

			{canManage && (
				<div className="mt-6 flex items-center justify-end gap-3">
					<button
						type="button"
						disabled={loading}
						onClick={handleReopen}
						className="py-1 pt-2 w-[150px] rounded-xl bg-white border border-black font-semibold hover:bg-black hover:text-white disabled:opacity-50"
					>
						Reopen
					</button>
					<button
						type="button"
						disabled={loading}
						onClick={handleSave}
						className="py-1 pt-2 w-[150px] rounded-xl bg-black text-white border border-black font-semibold hover:opacity-80 disabled:opacity-50"
					>
						Save Changes
					</button>
				</div>
			)}
		</>
	);
};

export default DetailProjectCloseModal;
